import React, { useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import { EyeOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { formatDate } from '../utils/formatters';
import { getCertificateStatus, getDaysUntilExpiry } from '../utils/certificateHelpers';
import { brandSemantic } from '../config/brand';
import SoftTag from '../components/SoftTag';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_TONES = {
  Expired: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  'Expiring Soon': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Valid: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

const SCOPE_TONES = {
  Supplier: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
  Product: { color: brandSemantic.primary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
};

export default function CompliancePage({
  certificates = [],
  suppliers = [],
  products = [],
  openSupplierProfile,
  onOpenProduct,
  onVerifyCertificate,
}) {
  const { t } = useI18n();
  const [searchText, setSearchText] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);

  const getSupplier = (supplierId) => suppliers.find((s) => s.id === supplierId);
  const getProduct = (productId) => products.find((p) => p.id === productId);

  const documentTypeOptions = Array.from(new Set(certificates.map((cert) => cert.documentType).filter(Boolean)));

  const enrichedCertificates = certificates.map((cert) => ({
    ...cert,
    supplierName: getSupplier(cert.supplierId)?.name || '',
    productName: getProduct(cert.productId)?.name || '',
    status: getCertificateStatus(cert.expiryDate),
    daysUntilExpiry: getDaysUntilExpiry(cert.expiryDate),
    needsVerification: Boolean(cert.inherited) && !cert.verifiedAt,
  }));

  const query = searchText.trim().toLowerCase();
  const filteredCertificates = enrichedCertificates.filter((cert) => {
    if (documentTypeFilter !== 'all' && cert.documentType !== documentTypeFilter) return false;
    if (statusFilter !== 'all' && cert.status !== statusFilter) return false;
    if (scopeFilter !== 'all' && (cert.scope || 'Supplier') !== scopeFilter) return false;
    if (verificationFilter === 'needsVerification' && !cert.needsVerification) return false;
    if (!query) return true;
    return (
      cert.supplierName.toLowerCase().includes(query)
      || cert.productName.toLowerCase().includes(query)
      || cert.name.toLowerCase().includes(query)
      || (cert.documentType || '').toLowerCase().includes(query)
      || (cert.standard || '').toLowerCase().includes(query)
    );
  });

  const columns = [
    {
      title: t('Document / Certificate'),
      key: 'document',
      render: (_, record) => (
        <Space align="start">
          <FilePdfOutlined style={{ color: brandSemantic.error, fontSize: 20, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.fileName}</Text>
            {record.needsVerification && (
              <Tag color="gold" style={{ marginTop: 4, display: 'block', width: 'fit-content' }}>
                {t('Inherited — verification required')}
              </Tag>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: t('Scope'),
      key: 'scope',
      width: 90,
      render: (_, record) => <SoftTag tone={SCOPE_TONES[record.scope] || SCOPE_TONES.Supplier}>{t(record.scope || 'Supplier')}</SoftTag>,
    },
    {
      title: t('Supplier Name'),
      key: 'supplierName',
      render: (_, record) => {
        const supplier = getSupplier(record.supplierId);
        if (!supplier) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Button type="link" onClick={() => openSupplierProfile?.(supplier, 'compliance')} style={{ padding: 0 }}>
            {supplier.name}
          </Button>
        );
      },
    },
    {
      title: t('Product'),
      key: 'product',
      render: (_, record) => {
        const product = getProduct(record.productId);
        if (!product) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Button type="link" onClick={() => onOpenProduct?.(product)} style={{ padding: 0 }}>
            {product.name}
          </Button>
        );
      },
    },
    {
      title: t('Type / Standard'),
      key: 'typeStandard',
      render: (_, record) => (
        <div>
          <div>{record.documentType || <span style={{ color: '#bfbfbf' }}>—</span>}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.standard}</Text>
        </div>
      ),
    },
    {
      title: t('Valid From'),
      dataIndex: 'validFrom',
      key: 'validFrom',
      width: 120,
      render: (value) => (value ? formatDate(value) : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Valid To'),
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (value) => formatDate(value),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <SoftTag tone={STATUS_TONES[status] || STATUS_TONES.Valid}>{t(status)}</SoftTag>,
    },
    {
      title: t('Expires In'),
      key: 'expiresIn',
      width: 110,
      render: (_, record) => (
        record.daysUntilExpiry != null
          ? <Text style={{ color: (STATUS_TONES[record.status] || STATUS_TONES.Valid).color, fontWeight: 600 }}>{t('{{count}} days', { count: record.daysUntilExpiry })}</Text>
          : <span style={{ color: '#bfbfbf' }}>—</span>
      ),
    },
    {
      title: t('Uploaded By'),
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 150,
    },
    {
      title: t('Action'),
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            style={{ padding: 0 }}
            onClick={() => setPreviewFile({ url: record.fileUrl, name: record.fileName })}
          >
            {t('Preview')}
          </Button>
          {record.needsVerification && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => onVerifyCertificate?.(record.id)}
            >
              {t('Verify Document')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Compliance')}</Title>
        <div className="page-toolbar-controls">
          <Input
            placeholder={t('Search by supplier, document or certificate type...')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 320, height: 32, display: 'flex', alignItems: 'center' }}
            suffix={<SearchOutlined />}
          />
          <Select value={documentTypeFilter} onChange={setDocumentTypeFilter} style={{ width: 180 }}>
            <Option value="all">{t('All Document Types')}</Option>
            {documentTypeOptions.map((type) => <Option key={type} value={type}>{type}</Option>)}
          </Select>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">{t('All Statuses')}</Option>
            <Option value="Valid">{t('Valid')}</Option>
            <Option value="Expiring Soon">{t('Expiring Soon')}</Option>
            <Option value="Expired">{t('Expired')}</Option>
          </Select>
          <Select value={scopeFilter} onChange={setScopeFilter} style={{ width: 140 }}>
            <Option value="all">{t('All Scopes')}</Option>
            <Option value="Supplier">{t('Supplier')}</Option>
            <Option value="Product">{t('Product')}</Option>
          </Select>
          <Select value={verificationFilter} onChange={setVerificationFilter} style={{ width: 170 }}>
            <Option value="all">{t('All Documents')}</Option>
            <Option value="needsVerification">{t('Needs verification')}</Option>
          </Select>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredCertificates}
        rowKey="id"
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
        locale={{ emptyText: t('No certificates match') }}
      />

      <Modal
        title={previewFile?.name}
        open={Boolean(previewFile)}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width="80%"
        styles={{ body: { padding: 0, height: '80vh' } }}
        destroyOnClose
      >
        {previewFile && (
          <iframe
            src={previewFile.url}
            title={previewFile.name}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        )}
      </Modal>
    </div>
  );
}
